import { DecimalPipe, TitleCasePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    computed,
    inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
    ArrowLeftRight,
    Headset,
    KeyRound,
    LogOut,
    LucideAngularModule,
    Settings,
    Webhook,
} from 'lucide-angular';

import { TRANSLATION_KEYS } from '@core/internationalization/translations_keys';
import { AccountService } from '@core/services/admin/account.service';
import { LoggedUserService } from '@core/services/admin/logged-user.service';
import { AuthService } from '@features/auth/auth.service';
import { BILLING_SLUGS } from '@features/settings/billing/billing.routes';
import { BillingService } from '@features/settings/billing/services/billing.service';
import { StripeInternalHttpService } from '@core/services/admin/stripe-internal.http.service';
import { APP_SLUGS, MAIN_SLUGS, SETTINGS_SLUGS } from '@routes/routes.types';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { UserAvatarComponent } from '@shared/components/user-avatar/user-avatar.component';

/**
 * Floating user popover anchored under the avatar button in the topbar.
 *
 * Sections (top → bottom):
 *   1. Identity header — avatar + name + email.
 *   2. Consumo card — black "Mejorar" CTA + MACs used / contracted counters and usage bar.
 *   3. Workspace card — workspace name + plan label + switch affordance.
 *   4. Settings entry.
 *   5. API keys + Webhooks entries (only when the account has the public API).
 *   6. Logout entry.
 *
 * Closing is delegated to the parent (the topbar owns the outside-click /
 * escape handlers, in the same shape as the bell popover).
 */
@Component({
    selector: 'app-user-menu',
    standalone: true,
    imports: [
        DecimalPipe,
        TitleCasePipe,
        LucideAngularModule,
        TranslateModule,
        UserAvatarComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'block' },
    templateUrl: './user-menu.component.html',
})
export class UserMenuComponent {
    private readonly router = inject(Router);
    private readonly authS = inject(AuthService);
    private readonly userS = inject(LoggedUserService);
    private readonly accountS = inject(AccountService);
    private readonly billingS = inject(BillingService);
    private readonly stripeInternalHttpS = inject(StripeInternalHttpService);
    private readonly toastr = inject(ToastrService);

    @Input() isOpen = false;
    @Output() close = new EventEmitter<void>();

    protected readonly T = TRANSLATION_KEYS.common.userMenu;
    protected readonly C = TRANSLATION_KEYS.common;

    protected readonly SettingsIcon = Settings;
    protected readonly ApiKeysIcon = KeyRound;
    protected readonly WebhooksIcon = Webhook;
    protected readonly LogoutIcon = LogOut;
    protected readonly SwitchIcon = ArrowLeftRight;
    protected readonly HeadsetIcon = Headset;

    // -- Identity ------------------------------------------------------------

    protected readonly profileImg = computed(() => this.userS.user()?.avatar);
    protected readonly name = computed(() => this.userS.user()?.name);
    protected readonly email = computed(() => this.userS.user()?.email);

    // -- Workspace / plan ----------------------------------------------------

    protected readonly accountName = computed(() => this.accountS.account()?.account_name);

    /** KAN-253 — the API-keys entry only exists when the account has the public API contracted. */
    protected readonly hasApiAccess = computed(() => !!this.accountS.account()?.subscription?.api);

    // -- Custom (ad-hoc) pricing ---------------------------------------------
    // Mirrors settings/account: when the subscription carries `custom_price_ids`
    // the account is on a negotiated budget plan — subscribing checks out
    // directly with those prices, and upgrading is replaced by "contact sales".

    protected readonly customPriceIds = computed(
        () => this.accountS.account()?.subscription?.custom_price_ids ?? []
    );
    protected readonly hasCustomPricing = computed(() => this.customPriceIds().length > 0);
    protected readonly isSubscribed = computed(() => !!this.billingS.currentSubscription());
    protected readonly planLabel = computed(() => {
        const product = this.billingS.currentPlan()?.product_name;
        if (product) return product;
        const type = this.billingS.currentPlanVariant();
        return type ?? '';
    });

    // -- Consumo (AI-managed contacts — MAC) ---------------------------------
    // Same metric as Settings → "Contactos gestionados por IA" and the dashboard:
    // the `mac_ai` meter consumed vs the plan's AI quota (the limit).

    /** Contracted limit: AI-managed-contacts quota included in the current plan. */
    protected readonly macsTotal = this.billingS.macAiQuota;

    /** AI-managed contacts consumed so far this period (the `mac_ai` meter). */
    protected readonly macsConsumed = computed(() =>
        Number(this.billingS.currentMacs()?.['mac_ai'] ?? 0)
    );

    /** Used / contracted ratio (0–1), mirrors the dashboard MAC usage bar. */
    protected readonly usagePct = computed(() => {
        const total = this.macsTotal();
        if (!total) return 0;
        return Math.min(1, this.macsConsumed() / total);
    });

    // -- Actions -------------------------------------------------------------

    protected handleUpgrade() {
        // Custom budget plan (not yet subscribed): skip plan selection and check
        // out directly with the negotiated Stripe prices via the *hosted* Stripe
        // checkout (so Stripe creates the customer) — same as
        // settings/account's `handleSubscribeNow()`.
        if (this.hasCustomPricing()) {
            this.stripeInternalHttpS.initCheckout(this.customPriceIds(), true).subscribe({
                next: url => (window.location.href = url),
                error: (err: HttpErrorResponse) =>
                    this.toastr.error(err?.error?.message || 'Failed to initiate checkout'),
            });
            return;
        }

        this.close.emit();

        // Mirrors settings/account's `handleSubscribeNow()` — always routes to the
        // subscribe wizard rather than going through `billingS.goToSubcribe()`,
        // which branches into a modal for certain plan states.
        this.router.navigateByUrl(
            `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.SETTINGS}/${SETTINGS_SLUGS.billing}/${BILLING_SLUGS.subscribe}`
        );
    }

    /**
     * Custom budget plan + already subscribed: self-service upgrade/cancel is
     * disabled, so route the user to settings/account where the full
     * "contact sales" panel (email / phone / WhatsApp) lives.
     */
    protected handleContactSales() {
        this.close.emit();
        this.router.navigateByUrl(
            `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.SETTINGS}/${SETTINGS_SLUGS.account}`
        );
    }

    protected handleSettings() {
        this.close.emit();
        this.router.navigateByUrl(
            `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.SETTINGS}/${SETTINGS_SLUGS.account}`
        );
    }

    protected handleApiKeys() {
        this.close.emit();
        this.router.navigateByUrl(
            `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.SETTINGS}/${SETTINGS_SLUGS.apiKeys}`
        );
    }

    protected handleWebhooks() {
        this.close.emit();
        this.router.navigateByUrl(
            `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.SETTINGS}/${SETTINGS_SLUGS.webhooks}`
        );
    }

    protected handleLogout() {
        this.close.emit();
        this.authS.logout();
    }
}
